import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Folder, Loader2 } from 'lucide-react';
import { Collection, getCollections, getFileCollections, addFileToCollection, removeFileFromCollection, createCollection } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';

interface AddToCollectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  fileIds: string[];
  onUpdate?: () => void;
}

const AddToCollectionModal: React.FC<AddToCollectionModalProps> = ({
  isOpen,
  onClose,
  fileIds,
  onUpdate,
}) => {
  const isMulti = fileIds.length > 1;
  const navigate = useNavigate();
  const { getValidToken } = useAuth();
  const { showToast } = useToast();
  const [collections, setCollections] = useState<Collection[]>([]);
  const [fileCollectionIds, setFileCollectionIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [processingIds, setProcessingIds] = useState<Set<string>>(new Set());
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newCollectionName, setNewCollectionName] = useState('');
  const [isCreating, setIsCreating] = useState(false);

  // Stash latest values in refs so the open-effect can use them without
  // re-firing on every parent render. The parent re-renders frequently
  // (e.g. Player ticks `currentTime` per audio frame) and was passing a
  // fresh `fileIds` array literal each time, which previously turned the
  // load into an infinite loop.
  const fileIdsRef = useRef(fileIds);
  fileIdsRef.current = fileIds;
  const getValidTokenRef = useRef(getValidToken);
  getValidTokenRef.current = getValidToken;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const token = await getValidTokenRef.current();
        if (!token) {
          if (!cancelled) {
            showToastRef.current('Please sign in to manage collections', 'warning');
            onCloseRef.current();
          }
          return;
        }

        const ids = fileIdsRef.current;

        // For single-file mode, fetch the file's current collections so we can
        // show which ones it's already in. For multi-file mode (post-upload),
        // skip this — files are brand new, in zero collections, pure add mode.
        if (ids.length === 1) {
          const [allCollections, fileCollections] = await Promise.all([
            getCollections(token),
            getFileCollections(ids[0], token),
          ]);
          if (cancelled) return;
          setCollections(allCollections);
          setFileCollectionIds(new Set(fileCollections.map(c => c.collection_id)));
        } else {
          const allCollections = await getCollections(token);
          if (cancelled) return;
          setCollections(allCollections);
          setFileCollectionIds(new Set());
        }
      } catch (error) {
        console.error('Failed to load collections:', error);
        if (!cancelled) showToastRef.current('Failed to load collections', 'error');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const handleToggleCollection = async (collectionId: string, isCurrentlyInCollection: boolean) => {
    setProcessingIds(prev => new Set(prev).add(collectionId));

    try {
      const token = await getValidToken();
      if (!token) {
        showToast('Please sign in to manage collections', 'warning');
        return;
      }

      if (isCurrentlyInCollection) {
        // Remove from collection (only reachable in single-file mode)
        await Promise.all(fileIds.map(id => removeFileFromCollection(id, collectionId, token)));
        setFileCollectionIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(collectionId);
          return newSet;
        });
        showToast('Removed from collection', 'success');
      } else {
        // Add all selected files to the collection
        await Promise.all(fileIds.map(id => addFileToCollection(id, collectionId, token)));
        setFileCollectionIds(prev => new Set(prev).add(collectionId));
        const count = fileIds.length;
        showToast(
          count === 1
            ? 'Added to collection'
            : `Added ${count} tracks to collection`,
          'success'
        );
      }

      if (onUpdate) {
        onUpdate();
      }

      // In multi-file mode, close the sheet after a successful add — the
      // user's intent is "drop these into a collection," not "manage a file."
      if (isMulti && !isCurrentlyInCollection) {
        onClose();
      }
    } catch (error) {
      console.error('Failed to toggle collection:', error);
      showToast(
        isCurrentlyInCollection ? 'Failed to remove from collection' : 'Failed to add to collection',
        'error'
      );
    } finally {
      setProcessingIds(prev => {
        const newSet = new Set(prev);
        newSet.delete(collectionId);
        return newSet;
      });
    }
  };

  // Quick-create: name only, private, no artwork. Adds the current track and
  // navigates to the new collection. User can refine metadata from there.
  const handleQuickCreate = async () => {
    const name = newCollectionName.trim();
    if (!name || isCreating) return;

    setIsCreating(true);
    try {
      const token = await getValidToken();
      if (!token) {
        showToast('Please sign in to create collections', 'warning');
        return;
      }

      const newCollection = await createCollection(name, undefined, 'private', token);
      await Promise.all(
        fileIds.map(id => addFileToCollection(id, newCollection.collection_id, token))
      );

      showToast(`Collection "${name}" created`, 'success');
      if (onUpdate) onUpdate();
      onClose();
      navigate(`/collection/${newCollection.collection_id}`);
    } catch (error) {
      console.error('Failed to create collection:', error);
      showToast('Failed to create collection', 'error');
    } finally {
      setIsCreating(false);
    }
  };

  const cancelCreateForm = () => {
    setShowCreateForm(false);
    setNewCollectionName('');
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop — above FloatingPlayer (z-90) */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 z-[100]"
            onClick={onClose}
          />

          {/* Sheet — bottom-anchored on mobile, floating card on desktop.
              Slide-up animation works on both breakpoints. */}
          <motion.div
            initial={{ y: '100%' }}
            animate={{ y: 0 }}
            exit={{ y: '100%' }}
            transition={{ type: 'spring', damping: 30, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 z-[110] md:left-1/2 md:right-auto md:-translate-x-1/2 md:max-w-md md:w-full md:bottom-6 bg-gray-900 border border-gray-800 shadow-2xl rounded-t-2xl md:rounded-2xl max-h-[85vh] flex flex-col"
          >
            {/* Drag handle — mobile-only affordance */}
            <div className="md:hidden flex justify-center pt-2 pb-1">
              <div className="w-10 h-1 rounded-full bg-white/20" />
            </div>

            {/* Header */}
            <div className="flex items-center justify-between p-4 md:p-6 border-b border-gray-800">
              <h2 className="text-xl font-semibold text-white">
                {isMulti ? `Add ${fileIds.length} tracks to collection` : 'Add to Collection'}
              </h2>
              <button
                onClick={onClose}
                className="text-gray-400 hover:text-white transition-colors"
              >
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-4 md:p-6">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-neon-500"></div>
                </div>
              ) : collections.length === 0 ? (
                <div className="text-center py-12">
                  <Folder className="w-12 h-12 text-gray-600 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">You don't have any collections yet</p>
                  <p className="text-sm text-gray-500">Create your first collection to get started</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {collections.map((collection) => {
                    const isInCollection = fileCollectionIds.has(collection.collection_id);
                    const isProcessing = processingIds.has(collection.collection_id);

                    return (
                      <motion.label
                        key={collection.collection_id}
                        className={`flex items-center gap-3 p-4 rounded-lg border cursor-pointer transition-all ${
                          isInCollection
                            ? 'bg-neon-500/10 border-neon-500/30'
                            : 'bg-gray-800/50 border-gray-700 hover:border-gray-600'
                        }`}
                        whileHover={{ scale: 1.01 }}
                        whileTap={{ scale: 0.99 }}
                      >
                        <input
                          type="checkbox"
                          checked={isInCollection}
                          disabled={isProcessing}
                          onChange={() => handleToggleCollection(collection.collection_id, isInCollection)}
                          className="w-5 h-5 rounded border-gray-600 text-neon-500 focus:ring-neon-500 focus:ring-offset-0 focus:ring-2 bg-gray-700 disabled:opacity-50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium text-white truncate">
                              {collection.collection_name}
                            </p>
                            {isProcessing && (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-neon-500"></div>
                            )}
                          </div>
                          {collection.description && (
                            <p className="text-sm text-gray-400 truncate">{collection.description}</p>
                          )}
                          <p className="text-xs text-gray-500 mt-1">
                            {collection.file_count} {collection.file_count === 1 ? 'file' : 'files'}
                          </p>
                        </div>
                      </motion.label>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Footer — inline create form, or trigger button. The full
                create-with-artwork experience lives on the collection page; here
                we only need a name to give the user somewhere to land. */}
            <div className="p-4 md:p-6 border-t border-gray-800">
              {showCreateForm ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={newCollectionName}
                    onChange={(e) => setNewCollectionName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleQuickCreate();
                      if (e.key === 'Escape') cancelCreateForm();
                    }}
                    placeholder="Collection name"
                    autoFocus
                    disabled={isCreating}
                    className="w-full px-4 py-3 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-neon-500 focus:border-transparent disabled:opacity-50"
                  />
                  <div className="flex gap-2">
                    <button
                      onClick={cancelCreateForm}
                      disabled={isCreating}
                      className="flex-1 px-4 py-2 rounded-lg bg-gray-800 hover:bg-gray-700 text-white border border-gray-700 disabled:opacity-50 transition-colors"
                    >
                      Cancel
                    </button>
                    <motion.button
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={handleQuickCreate}
                      disabled={!newCollectionName.trim() || isCreating}
                      className="flex-1 px-4 py-2 rounded-lg bg-neon-500 hover:bg-neon-600 text-white font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 transition-colors"
                    >
                      {isCreating ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Creating…
                        </>
                      ) : (
                        'Create & open'
                      )}
                    </motion.button>
                  </div>
                </div>
              ) : (
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  onClick={() => setShowCreateForm(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-800 hover:bg-gray-700 text-white rounded-lg transition-colors border border-gray-700"
                >
                  <Plus className="w-5 h-5" />
                  Create new collection
                </motion.button>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default AddToCollectionModal;
